import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { colors, frames, useMediaQuery } from '../../../../Styles';
import { PageHeader, FeedCurveCard, Overlay, NewButtonList } from '../../../../Components';
import { NavigationFunction as RootNavigation } from "../../../../Navigation/root"
import { GlobalMenu_Pages as Pages } from "../../../../Navigation/pages"
import { RootState, useAppSelector } from '../../../../Store';
import { FeedCurve, Graph_QL } from '../../../../Services/Agrisys_GqlDataCaller';
import { useFeedCurves } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves';
import { ListStyle_Types, NewInputHandles } from '../../../../Definitions';
import CardsView from '../../../../Components/BaseComponents/cardsView';
import NewFeedcurve from '../../../../Components/FeedCurves/Modals/newFeedCurve';
import ShowFeedCurve from '../../../../Components/FeedCurves/Modals/showFeedCurve';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SET_EDIT_MODE } from '../../../../Store/UI/editModeSlice';
import { useFeedcurveOptions } from '../../../../Components/_Components';
import { SelectAllFeedCurves } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice';

export default function FeedCurves() {
  const { t } = useTranslation()
  const dispatchEditMode = useDispatch();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);
  const navigator = useNavigate()
  const [selectedFeedcurve, setSelectedFeedcurve] = useState<FeedCurve | undefined>(undefined);
  const [modalState, setModalState] = useState<"ShowFeedCurve" | "NewFeedcurve" | undefined>(undefined);
  const mobileXs = useMediaQuery(`(max-width: 690px)`);

  // #region | Data
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = Graph_QL.ObjToString({domain: farm ?? ""});
  const connection = Graph_QL.create({domain: farm ?? ""});

  const {call: getFeedCurves, createFeedCurve, copyFeedCurve} = useFeedCurves(connection);
  const feedCurveNameInputRef = useRef<NewInputHandles>(null);
  const {feedCurveOptions} = useFeedcurveOptions();
  
  const feedCurvesData = useAppSelector(SelectAllFeedCurves);

  useEffect(() => {
    if (!feedCurvesData.length || (feedCurvesData.length > 0 && ( !feedCurvesData.find((feedCurves) => feedCurves.milestones !== undefined) || feedCurveOptions.length > feedCurvesData.length))) {
      getFeedCurves({fetchAmount: 50, ifFeedCurveInfo: true, ifSpreading: true})
    }
  },[domain, feedCurveOptions.length, feedCurvesData, getFeedCurves])
  
  const feedCurvesIds = useMemo(() => {
    return feedCurvesData.map((feedCurve) => feedCurve.feedCurveId)
  },[feedCurvesData])
  // #endregion

  // #region | On Feedcurve Select
  const onFeedCurveSelect = (id: number) => {
    const selected = feedCurvesData.find(fc => fc.feedCurveId === id);
    if (!selected) return;
  
    setSelectedFeedcurve(selected);
  
    if (editMode) {
      setModalState("NewFeedcurve");
    } else if (mobileXs) {
      RootNavigation({ Navigator: navigator, navigation_page: Pages.feedCurves, navigation_tab: Pages.feedCurve, replace: true, navArgs: { selected: selectedFeedcurve?.feedCurveId }, currentDomain: farm })
    } else {
      setModalState("ShowFeedCurve");
    }
  }
  // #endregion

  // #region | Handle Create Feedcurve
  const handleCreateFeedCurve = async () => {
    if (feedCurveNameInputRef.current?.value) {
      setModalState(undefined);
      setSelectedFeedcurve(undefined);
      // setCopyFeedCurveMode(false);
      dispatchEditMode(SET_EDIT_MODE(false))
      const response = modalState === "NewFeedcurve" && selectedFeedcurve ? await copyFeedCurve(feedCurveNameInputRef.current.value.toString(), selectedFeedcurve.feedCurveId) : await createFeedCurve(feedCurveNameInputRef.current.value.toString());
      if (response.success) {
        dispatchEditMode(SET_EDIT_MODE(false));
        RootNavigation({ Navigator: navigator, navigation_page: Pages.feedCurves, navigation_tab: Pages.feedCurve, replace: true, navArgs: { selected: response.feedcurve_id }, currentDomain: farm })
      }
    }
  }
  // #endregion

  // #region | Feedcurve Modal
  const FeedCurveModal = useMemo(() => {
    if (modalState === "ShowFeedCurve" && selectedFeedcurve && farm)

      return (<ShowFeedCurve
        farm={farm}
        navigateButton
        feedCurve={selectedFeedcurve}
        handleClose={() => setModalState(undefined)}
        feedCurveMap={{
          onNext: () => {
            const nextIndex = (feedCurvesIds.indexOf(selectedFeedcurve?.feedCurveId!) + 1) % feedCurvesIds.length;
            const next = feedCurvesData[nextIndex];
            if (next) setSelectedFeedcurve(next);
          },
          onPrev: () => {
            const prevIndex = (feedCurvesIds.indexOf(selectedFeedcurve?.feedCurveId!) - 1 + feedCurvesIds.length) % feedCurvesIds.length;
            const prev = feedCurvesData[prevIndex];
            if (prev) setSelectedFeedcurve(prev);
          }
        }}
      />
      )
    else return null;
  }, [farm, feedCurvesData, feedCurvesIds, modalState, selectedFeedcurve])
  // #endregion

  return (
    <frames.Page_inner>
      {editMode ? <Overlay overrides={{ }} beforeUnload onClickAlert /> : <></>}
      <PageHeader
        zIndex={99}
        topHeader={t(`menuHeaders.${Pages.feedCurves}`, { defaultValue: "feed curves" })}
        subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
        optionButtons={{
          contents: [t("feedCurvePages.feedCurves.copyFeedCurve", {defaultValue: "copy feed curve"})],
          onClick: (i => {
            if (i === 0) { dispatchEditMode(SET_EDIT_MODE(editMode ? false : true))}
          })
        }}
      />

      <frames._page_inner_content $hasScroll>
        <frames._page_inner_content_scroll>
          <div style={{ position: editMode ? "relative" : "unset", zIndex: editMode ? 9999 : 0 }}>
            <CardsView
              addExtraHeight
              items={feedCurvesData.map((item, index) => (
                <FeedCurveCard
                  key={index}
                  feedCurve={item}
                  onFeedCurveSelect={onFeedCurveSelect}
                  farm={farm}
                  expand={editMode ? false : true}
                />
              ))}
              cardBtn={{
                content: t("feedCurvePages.feedCurves.createFeedCurve", { defaultValue: "create feed curve" }),
                subContent: t("feedCurvePages.feedCurves.createFeedCurveFromScratch", { defaultValue: "Create feed curve from scratch" }),
                onCardBtnClick: () => setModalState("NewFeedcurve"),
                disabled: editMode
              }}
            />
          </div>
        </frames._page_inner_content_scroll>
      </frames._page_inner_content>

      {/* Popup Modal for clicked feedcurve */}
      {FeedCurveModal}

      {/* Add / Copy Feed Curve Modal */}
      {modalState === "NewFeedcurve" &&
        <NewFeedcurve
          copyFeedcurve={selectedFeedcurve}
          handleClose={() => {setModalState(undefined); setSelectedFeedcurve(undefined)}}
          handleCreateFeedCurve={handleCreateFeedCurve}
          feedCurveNameInputRef={feedCurveNameInputRef}
        />
      }

      {editMode && createPortal(
        <div style={{ position: "absolute", zIndex: 9999, bottom: "40px", left: "50%", transform: "translateX(-50%)", backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d", }}>
          <NewButtonList
            contents={[t("buttons.cancel", { defaultValue: "cancel" })]}
            onClick={(i) => {
              if (i === 0) { dispatchEditMode(SET_EDIT_MODE(false))}
            }}
            overrides={(i) => ({
              baseStyles: { backgroundColor: colors.greyDarkPrimary, border: "3px solid " + colors.blueDarkPrimary },
              hoverStyles: { backgroundColor: colors.blueDarkPrimary },
            })}
            listOverrides={{
              listStyle: mobileXs ? ListStyle_Types.vertical : ListStyle_Types.horizontal
            }}
          />
        </div>, document.body)}
    </frames.Page_inner>
  );
}  

// import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router';
// import { frames, useMediaQuery } from '../../../../Styles';
// import { PageHeader, FeedCurveCard, NewModal, NewSelect, NewInput, NewButton } from '../../../../Components';
// import { NavigationFunction as RootNavigation } from "../../../../Navigation/root"
// import { GlobalMenu_Pages as Pages } from "../../../../Navigation/pages"
// import { useAppSelector } from '../../../../Store';
// import { FeedCurve, Graph_QL } from '../../../../Services/Agrisys_GqlDataCaller';
// import { useFeedCurves } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves';
// import { NewInputHandles } from '../../../../Definitions';
// import CardsView from '../../../../Components/BaseComponents/cardsView';
// import ShowFeedCurve from '../../../../Components/FeedCurves/Modals/showFeedCurve';

// export default function FeedCurves() {
//   const {t} = useTranslation()
//   const navigator = useNavigate()
//   const [error, setError] = useState<string>("");
//   const [selectedFeedcurve, setSelectedFeedcurve] = useState<FeedCurve | undefined>(undefined);
//   const [modalState, setModalState] = useState<"ShowFeedCurve" | "NewFeedcurve" | "CopyFeedcurve" | undefined>(undefined);
//   const mobileXs = useMediaQuery(`(max-width: 690px)`);
//   const touch = 'ontouchstart' in window

//   // #region | Data
//   const farm = useAppSelector(({activeData}) => activeData.farm)
//   const domain = Graph_QL.ObjToString({domain: farm ?? ""});
//   const connection = Graph_QL.create({domain: farm ?? ""});
 
//   const {call: getFeedCurves, data: feedCurvesState, createFeedCurve, copyFeedCurve} = useFeedCurves(connection);
//   const feedCurveNameInputRef = useRef<NewInputHandles>(null);
   
//   const feedCurvesData = useMemo(() => {
//     return feedCurvesState && feedCurvesState[domain] ? feedCurvesState[domain]?.data?.filter((feedCurve) => feedCurve.info?.deleted !== true) : [];
//     },[domain, feedCurvesState])
//     useEffect(() => {
//       if (!feedCurvesState[domain] || (feedCurvesState[domain].data.length > 0 && !feedCurvesState[domain].data.find((feedCurves) => feedCurves.milestones !== undefined))) {
//         getFeedCurves({fetchAmount: 50, ifFeedCurveInfo: true, ifSpreading: true})
//       }
//     },[domain, getFeedCurves, feedCurvesState])
   
//   const feedCurvesIds = useMemo(() => {
//     return feedCurvesData.map((feedCurve) => feedCurve.feedCurveId)
//   },[feedCurvesData])
//   // #endregion

//   // #region | On Feedcurve Select
//   const onFeedCurveSelect = (id: number) => {
//     const selected = feedCurvesData.find(fc => fc.feedCurveId === id);
//     if (!selected) return;
  
//     setSelectedFeedcurve(selected);
  
//     if (mobileXs) {
//       RootNavigation({ Navigator: navigator, navigation_page: Pages.feedCurves, navigation_tab: Pages.feedCurve, replace: true, navArgs: { selected: selected?.feedCurveId }, currentDomain: farm })
//     } else {
//       setModalState("ShowFeedCurve");
//     }
//   }
//   // #endregion
   
//   // #region | Handle Create Click
//   const handleCreateClick = async () => {
//     const inputEl = feedCurveNameInputRef.current;
//     if (!inputEl) return;

//     if (modalState === "CopyFeedcurve" && !selectedFeedcurve) {
//       setError(t("feedCurvePages.feedCurves.feedCurveMissing", {defaultValue: "feed curve missing"}));
//       setTimeout(() => setError(""), 2000);
//       return;
//     }

//     const name =  typeof inputEl.value === "string" ? inputEl.value.trim() : "";
//     if (!name) {
//       setError(t("feedCurvePages.feedCurves.feedCurveNameMissing", {defaultValue: "feed curve name missing"}));
//       setTimeout(() => setError(""), 2000);
//       return;
//     }
  
//     const response =
//       modalState === "CopyFeedcurve" && selectedFeedcurve
//         ? await copyFeedCurve(name, selectedFeedcurve.feedCurveId)
//         : modalState === "NewFeedcurve"
//         ? await createFeedCurve(name)
//         : undefined;
  
//     if (response?.success) {
//       RootNavigation({
//         Navigator: navigator,
//         navigation_page: Pages.feedCurves,
//         navigation_tab: Pages.feedCurve,
//         replace: true,
//         navArgs: { selected: response.feedcurve_id },
//         currentDomain: farm,
//       });
//     }
//   };
//   // #endregion
 
//   // #region | Feedcurve Modal
//    const FeedCurveModal = useMemo(() => {
//      if (modalState === "ShowFeedCurve" && selectedFeedcurve && farm)
 
//        return (<ShowFeedCurve
//          farm={farm}
//          navigateButton
//          feedCurve={selectedFeedcurve}
//          handleClose={() => setModalState(undefined)}
//          feedCurveMap={{
//            onNext: () => {
//              const nextIndex = (feedCurvesIds.indexOf(selectedFeedcurve?.feedCurveId!) + 1) % feedCurvesIds.length;
//              const next = feedCurvesData[nextIndex];
//              if (next) setSelectedFeedcurve(next);
//            },
//            onPrev: () => {
//              const prevIndex = (feedCurvesIds.indexOf(selectedFeedcurve?.feedCurveId!) - 1 + feedCurvesIds.length) % feedCurvesIds.length;
//              const prev = feedCurvesData[prevIndex];
//              if (prev) setSelectedFeedcurve(prev);
//            }
//          }}
//        />
//        )
//      else return null;
//    }, [farm, feedCurvesData, feedCurvesIds, modalState, selectedFeedcurve])
//    // #endregion

//   return (
//     <frames.Page_inner>
//       <PageHeader
//         zIndex={99}
//         topHeader={t(`menuHeaders.${Pages.feedCurves}`, { defaultValue: "feed curves" })}
//         subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
//         optionButtons={{
//           contents: [t("feedCurvePages.feedCurves.copyFeedCurve", {defaultValue: "copy feed curve"})],
//           onClick: (i => {
//             if (i === 0) {setModalState("CopyFeedcurve")}
//           })
//         }}
//       />

//       <frames._page_inner_content $hasScroll>
//         <frames._page_inner_content_scroll>
//           <CardsView
//             addExtraHeight={touch ? false : true}
//             items={feedCurvesData.map((item, index) => (
//               <FeedCurveCard
//                 key={index}
//                 feedCurve={item}
//                 onFeedCurveSelect={onFeedCurveSelect}
//                 farm={farm}
//                 expand
//               />
//             ))}
//             cardBtn={{
//               content: t("feedCurvePages.feedCurves.createFeedCurve", { defaultValue: "create feed curve" }),
//               subContent: t("feedCurvePages.feedCurves.createFeedCurveFromScratch", { defaultValue: "Create feed curve from scratch" }),
//               onCardBtnClick: () => setModalState("NewFeedcurve")
//             }}
//           />
//         </frames._page_inner_content_scroll>
//       </frames._page_inner_content>

//       {/* Popup Modal for clicked feedcurve */}
//       {FeedCurveModal}

//       {/* Copy Feed Curve Modal */}
//       {(modalState === "NewFeedcurve" || modalState === "CopyFeedcurve" ) &&
//         <NewModal
//           overrides={{
//             mainContentPadding: "0px 20px 20px 20px",
//           }}
//           predefinedButtons={{
//             onClose: () => { setModalState(undefined); setSelectedFeedcurve(undefined); feedCurveNameInputRef.current?.setValue("")}
//           }}
//           header={modalState === "NewFeedcurve" ? t("feedCurvePages.feedCurves.newFeedCurve", {defaultValue: "New Feed Curve"}) : t("feedCurvePages.feedCurves.copyFeedcurve", {defaultValue: "Copy Feed Curve"})}
//           mainContent={<div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "10px"}}>
//             {modalState === "CopyFeedcurve" && <NewSelect
//               header={t("feedCurvePages.feedCurves.copyFeedcurve", { defaultValue: "Copy excisting" })}
//               options={feedCurvesData.map((f) => { return { id: f.feedCurveId, label: f.name } })}
//               onChange={(i) => setSelectedFeedcurve(feedCurvesData.find(f => f.feedCurveId === Number(i?.id)))}
//               isRequired
//             />}
//             <NewInput
//               ref={feedCurveNameInputRef}
//               header={t("feedCurvePages.feedCurves.feedCurveName", { defaultValue: "Feed Curve Name" })}
//               valueOverrides={{
//                 valueType: "string",
//                 defaultValue: selectedFeedcurve !== undefined ? `${selectedFeedcurve.name} copy` : ""
//               }}
//               isRequired
//             />
//           </div>}
//           footerContent={
//             <NewButton
//               content={t("feedCurvePages.feedCurves.createFeedCurve", {defaultValue: "Create Feed Curve"})}
//               onClick={handleCreateClick}
//             />
//           }
//           footerMessage={error}
//         />
//       }
//     </frames.Page_inner>
//   );
// }  